/*
  Delivery checks: the Delivery page postcode checker and the product page
  "check delivery to your postcode" line. Both ask the waltons-delivery-rates
  Cloudflare worker (repo waltons-delivery-worker) for live Intuitive Shipping
  options, which differ by building and postcode.

  Worker URL: the theme setting "Delivery rates worker URL" (the Delivery page
  section can override it). Only the origin is used; the paths are fixed:
    /delivery-rate?postcode=&variant=  options for one building
    /delivery-zone?postcode=           zone read for the worker's default shed

  Delivery page, three tiers so the customer never sees a blank box:
    1. Live options per building type (section blocks, one product each), or
       the worker's zone read when no building blocks are set.
    2. Bundled zone map, assets/delivery-zones.json, when the worker is blank,
       slow or down: surcharge areas, and islands the team confirms by phone.
    3. Generic wording plus the phone number if even the zone map fails.

  Product page: renders only when the worker URL is set. The last postcode a
  visitor checked is remembered in this browser (localStorage). Opening a
  building shows a kept answer for it (up to 24 hours old) or the postcode
  ready in the field; changing size checks again. A lookup error says so and
  points to checkout or the phone; "cannot be booked online" is shown only when
  Intuitive Shipping genuinely returns no options (for example islands, or
  garden rooms outside the mainland zones).

  Mainland only products (data-direct, products tagged direct-dispatch by
  default): the product page answers from a blocked postcode list in Theme
  settings > Delivery rates, in the browser, with no worker or MGP call.
*/
(() => {
  if (window.WaltonsDelivery) return;

  const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;
  const UK_OUTWARD = /^[A-Z]{1,2}\d[A-Z\d]?$/;
  const STORAGE_KEY = 'waltons:delivery-postcode';
  // Shopify prices delivery to a full postcode only: an outward code alone
  // ("NG1") comes back with no options, which read as "not deliverable".
  const FULL_POSTCODE = 'Please enter your full postcode, for example NG24 1AB.';
  // The service can make two sequential requests with six-second limits.
  const LIVE_TIMEOUT_MS = 14000;
  const ZONES_TIMEOUT_MS = 7000;
  // Pick-a-day on: how long the standard rows wait, undated, for MGP's day
  // before the lead time estimate shows anyway. MGP usually answers first.
  const PAD_HOLD_MS = 2000;
  const memo = new Map();
  // Answers kept in this browser, so opening a building again (or another
  // page that asks the same thing) reuses the answer instead of asking the
  // worker. Every worker lookup that misses its own cache creates a hidden
  // Shopify cart, which Shopify counts as a visit with an add to cart.
  // Kept as long as the worker keeps them (src/index.js CACHE_SECONDS and
  // EMPTY_CACHE_SECONDS). Bump the key's version to drop them all after a
  // delivery price change.
  const ANSWERS_KEY = 'waltons:delivery-answers-v1';
  const ANSWER_MS = 24 * 3600 * 1000;
  const EMPTY_ANSWER_MS = 10 * 60 * 1000;
  const ANSWERS_MAX = 40;

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function normalise(raw) {
    const compact = String(raw || '').replace(/\s+/g, '').toUpperCase();
    if (UK_POSTCODE.test(compact)) return { compact, display: `${compact.slice(0, -3)} ${compact.slice(-3)}`, outward: compact.slice(0, -3), full: true };
    if (UK_OUTWARD.test(compact)) return { compact, display: compact, outward: compact, full: false };
    return null;
  }

  function workerOrigin(url) {
    if (!url) return '';
    try {
      return new URL(url).origin;
    } catch (e) {
      return '';
    }
  }

  function remember(postcode) {
    try { localStorage.setItem(STORAGE_KEY, postcode.display); } catch (e) { /* storage blocked */ }
  }

  function recall() {
    try { return normalise(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
  }

  function readAnswers() {
    try {
      const all = JSON.parse(localStorage.getItem(ANSWERS_KEY) || '{}');
      return all && typeof all === 'object' ? all : {};
    } catch (e) {
      return {};
    }
  }

  function storedAnswer(url) {
    const kept = readAnswers()[url];
    if (!kept || !kept.d || typeof kept.t !== 'number') return null;
    const age = Date.now() - kept.t;
    return age >= 0 && age < (kept.d.deliverable ? ANSWER_MS : EMPTY_ANSWER_MS) ? kept.d : null;
  }

  function keepAnswer(url, d) {
    try {
      const all = readAnswers();
      all[url] = { t: Date.now(), d };
      const urls = Object.keys(all).sort((a, b) => (all[b].t || 0) - (all[a].t || 0));
      urls.slice(ANSWERS_MAX).forEach((u) => delete all[u]);
      localStorage.setItem(ANSWERS_KEY, JSON.stringify(all));
    } catch (e) { /* storage blocked or full */ }
  }

  async function getJson(url) {
    if (memo.has(url)) return memo.get(url);
    const kept = storedAnswer(url);
    if (kept) {
      const answer = Promise.resolve(kept);
      memo.set(url, answer);
      return answer;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), LIVE_TIMEOUT_MS);
    const request = fetch(url, { signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`worker ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!d || typeof d.deliverable !== 'boolean' || !Array.isArray(d.options)
          || d.deliverable !== (d.options.length > 0)
          || d.options.some((o) => !o || typeof o.title !== 'string' || !Number.isFinite(o.price) || o.price < 0)) throw new Error('worker payload');
        keepAnswer(url, d);
        return d;
      })
      .finally(() => clearTimeout(timer));
    memo.set(url, request);
    request.catch(() => memo.delete(url));
    return request;
  }

  const rateUrl = (origin, variantId, postcode) =>
    `${origin}/delivery-rate?postcode=${encodeURIComponent(postcode.compact)}&variant=${encodeURIComponent(variantId)}`;

  const rates = (origin, variantId, postcode) => getJson(rateUrl(origin, variantId, postcode));

  const zone = (origin, postcode) =>
    getJson(`${origin}/delivery-zone?postcode=${encodeURIComponent(postcode.compact)}`);

  const isNextDayOption = (option) => /next\s+(working\s+)?day/i.test(option && option.title || '');

  // The small calendar leaf beside each delivery option: month over day,
  // read from the worded date ("24th October", "Tuesday 20th October").
  // Decorative, as the row's own text carries the date; without one it
  // shows a van.
  const VAN = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3.2v2.8h-7"/><circle cx="6.5" cy="17" r="1.6"/><circle cx="17" cy="17" r="1.6"/></svg>';
  function leaf(worded) {
    const m = String(worded || '').match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3})[a-z]*\b/);
    return m
      ? `<span class="wdel__leaf" aria-hidden="true"><span class="wdel__leaf-m">${escapeHtml(m[2])}</span><span class="wdel__leaf-d">${m[1]}</span></span>`
      : `<span class="wdel__leaf wdel__leaf--van" aria-hidden="true">${VAN}</span>`;
  }

  /*
    The "?" after "Choose your day at checkout, from <day>" (pick-a-day
    rows only) and the note it opens. MGP answers for the building alone;
    at checkout, pressure treatment (SI-040-*), installation (SI-INSTALL*)
    or a Ready Painted finish (PB-*) make the whole order one MGP's team
    arranges, with no calendar.
    The rows are rewritten with innerHTML, so the behaviour is bound once,
    on the document, below. One note is open at a time; it closes on the
    button again, Escape (focus back to the button), a tap, click or focus
    anywhere else (not a scroll), and whenever the checker redraws
    (closeHelpIn). The wording is Luke's.
  */
  const PICK_HELP = 'Adding pressure treatment, installation or a painted finish means our team arranges your delivery day.';
  const PICK_HELP_LABEL = 'Why your delivery day might be arranged by our team';
  let helpSeq = 0;
  let openHelp = null;
  let helpFrame = 0;

  // The row's date line with the button and its note. The day's last word
  // shares a no-wrap span with the button, so the circle never wraps onto
  // a line of its own on a phone.
  function pickWhen(day) {
    const id = `wdel-help-${++helpSeq}`;
    const words = String(day).split(' ');
    const last = words.pop();
    const lead = words.length ? `${escapeHtml(words.join(' '))} ` : '';
    return `<span class="wdel__when">Choose your day at checkout, from ${lead}<span class="wdel__keep">${escapeHtml(last)}<button type="button" class="wdel__ask" data-wdel-help aria-expanded="false" aria-controls="${id}" aria-label="${escapeHtml(PICK_HELP_LABEL)}">?</button></span></span>`
      + `<span class="wdel__tip" id="${id}" role="note" hidden>${escapeHtml(PICK_HELP)}</span>`;
  }

  // The note hangs below the button, across the row (never wider), with
  // its notch under the circle.
  function placeHelp() {
    if (!openHelp) return;
    const { button, note, row } = openHelp;
    if (!button.isConnected || !note.isConnected) return closeHelp(false);
    const rowBox = row.getBoundingClientRect();
    const buttonBox = button.getBoundingClientRect();
    const centre = buttonBox.left + buttonBox.width / 2 - rowBox.left;
    const width = note.offsetWidth;
    const left = Math.max(0, Math.min(centre - 28, rowBox.width - width));
    note.style.left = `${left}px`;
    note.style.top = `${buttonBox.bottom - rowBox.top + 9}px`;
    note.style.setProperty('--wdel-tip-x', `${Math.max(12, Math.min(centre - left, width - 12))}px`);
  }

  function onHelpResize() {
    cancelAnimationFrame(helpFrame);
    helpFrame = requestAnimationFrame(placeHelp);
  }

  function closeHelp(focusButton) {
    if (!openHelp) return;
    const { button, note, row } = openHelp;
    openHelp = null;
    cancelAnimationFrame(helpFrame);
    window.removeEventListener('resize', onHelpResize);
    button.setAttribute('aria-expanded', 'false');
    note.hidden = true;
    row.classList.remove('is-asking');
    if (focusButton && button.isConnected) button.focus();
  }

  // Called before a checker rewrites its result, so no note is left open
  // on (or pointing at) rows that are about to go.
  function closeHelpIn(container) {
    if (openHelp && (!openHelp.button.isConnected || container.contains(openHelp.button))) closeHelp(false);
  }

  function openHelpFor(button) {
    const note = document.getElementById(button.getAttribute('aria-controls'));
    const row = button.closest('.wdel__option');
    if (!note || !row) return;
    closeHelp(false);
    openHelp = { button, note, row };
    row.classList.add('is-asking');
    note.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    placeHelp();
    window.addEventListener('resize', onHelpResize);
    // Opened low on a phone screen: scroll just enough to show all of it
    // above the fixed Shop / Chat / Call bar, never taking the button off
    // the top.
    const below = Math.min(note.getBoundingClientRect().bottom + 12 - visibleBottom(), button.getBoundingClientRect().top - 8);
    if (below > 0) {
      const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({ top: below, behavior: still ? 'auto' : 'smooth' });
    }
  }

  // The lowest point on screen nothing fixed covers: the top of the phone
  // quick-action bar (snippets/waltons-mobile-bar.liquid) while it shows,
  // else the bottom of the window.
  function visibleBottom() {
    const bar = document.querySelector('.wmb');
    const box = bar && bar.getBoundingClientRect();
    return box && box.height > 0 && box.top < window.innerHeight ? box.top : window.innerHeight;
  }

  document.addEventListener('click', (e) => {
    const button = e.target instanceof Element && e.target.closest('[data-wdel-help]');
    if (!button) return;
    if (openHelp && openHelp.button === button) closeHelp(false);
    else openHelpFor(button);
  });
  // Pressed and let go outside the note and its button closes it, like a
  // popover. A touch that becomes a scroll ends in pointercancel, not
  // pointerup, so a phone can scroll to read the note without losing it.
  // Pointer events rather than click, because iOS sends no click to the
  // document for a tap on plain text.
  let pressedOutside = false;
  const outsideHelp = (e) => !!openHelp && !(e.target instanceof Node && (openHelp.button.contains(e.target) || openHelp.note.contains(e.target)));
  document.addEventListener('pointerdown', (e) => { pressedOutside = outsideHelp(e); });
  document.addEventListener('pointerup', (e) => {
    if (pressedOutside && outsideHelp(e)) closeHelp(false);
    pressedOutside = false;
  });
  document.addEventListener('pointercancel', () => { pressedOutside = false; });
  document.addEventListener('focusin', (e) => {
    if (openHelp && e.target !== openHelp.button) closeHelp(false);
  });
  document.addEventListener('keydown', (e) => {
    if (openHelp && (e.key === 'Escape' || e.key === 'Esc')) closeHelp(true);
  });

  function priorityDeliveryDate(cutoff, holidaysRaw, instant = new Date()) {
    if (!window.Intl || !Intl.DateTimeFormat) return '';
    const cutoffMatch = String(cutoff || '').trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (!cutoffMatch) return '';

    const cutoffMinutes = Number(cutoffMatch[1]) * 60 + Number(cutoffMatch[2]);
    const holidays = new Set(String(holidaysRaw || '').split(',').map((s) => s.trim()).filter(Boolean));
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = {};
    formatter.formatToParts(instant).forEach((part) => { parts[part.type] = part.value; });
    const iso = `${parts.year}-${parts.month}-${parts.day}`;
    const nowMinutes = (Number(parts.hour) % 24) * 60 + Number(parts.minute);

    const dayFrom = (offset) => {
      const date = new Date(`${iso}T12:00:00Z`);
      date.setUTCDate(date.getUTCDate() + offset);
      return date;
    };
    const isoOf = (date) => date.toISOString().slice(0, 10);
    const isWorkingDay = (date) => date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !holidays.has(isoOf(date));
    const nextWorkingDay = (fromOffset) => {
      for (let offset = fromOffset; offset < fromOffset + 14; offset += 1) {
        const date = dayFrom(offset);
        if (isWorkingDay(date)) return { date, offset };
      }
      return null;
    };

    const today = dayFrom(0);
    let delivery;
    if (isWorkingDay(today) && nowMinutes < cutoffMinutes) {
      delivery = nextWorkingDay(1);
    } else {
      const nextOrderDay = nextWorkingDay(1);
      if (!nextOrderDay) return '';
      delivery = nextWorkingDay(nextOrderDay.offset + 1);
    }
    if (!delivery) return '';

    const day = delivery.date.getUTCDate();
    const mod100 = day % 100;
    const mod10 = day % 10;
    let suffix = 'th';
    if (mod100 < 11 || mod100 > 13) {
      if (mod10 === 1) suffix = 'st';
      else if (mod10 === 2) suffix = 'nd';
      else if (mod10 === 3) suffix = 'rd';
    }
    const month = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long' }).format(delivery.date);
    return `${day}${suffix} ${month}`;
  }

  // Whether it is a working day before the next day cut-off, UK time (the
  // same settings priorityDeliveryDate uses). MGP refuses next day for
  // every product once its cut-off has passed, so its refusal only says
  // something about the product before then.
  function beforeCutoff(cutoff, holidaysRaw, instant = new Date()) {
    if (!window.Intl || !Intl.DateTimeFormat) return false;
    const cutoffMatch = String(cutoff || '').trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (!cutoffMatch) return false;
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(instant).forEach((part) => { parts[part.type] = part.value; });
    const iso = `${parts.year}-${parts.month}-${parts.day}`;
    const weekday = new Date(`${iso}T12:00:00Z`).getUTCDay();
    const holidays = String(holidaysRaw || '').split(',').map((s) => s.trim());
    if (weekday === 0 || weekday === 6 || holidays.includes(iso)) return false;
    return (Number(parts.hour) % 24) * 60 + Number(parts.minute) < Number(cutoffMatch[1]) * 60 + Number(cutoffMatch[2]);
  }

  /*
    Intuitive Shipping rate titles are internal ("Palletised Shipping - Sheds -
    Zone A", "Log Cabins - Zone C"), so customers see plain labels instead:
    free, next working day, or delivery with its price. Duplicates collapse
    (a cabin can return two free rates).
  */
  function describeOptions(options) {
    const seen = new Set();
    const parts = options
      .map((o) => {
        const nextDay = isNextDayOption(o);
        if (nextDay) return { price: o.price, rank: 0, text: o.price === 0 ? 'free next working day delivery' : `next working day delivery <b>£${o.price.toFixed(2)}</b>` };
        if (o.price === 0) return { price: 0, rank: 1, text: 'free standard delivery' };
        return { price: o.price, rank: 1, text: `standard delivery <b>£${o.price.toFixed(2)}</b>` };
      })
      .sort((a, b) => a.rank - b.rank || a.price - b.price)
      .filter((o) => (seen.has(o.text) ? false : seen.add(o.text)))
      .map((o) => o.text);
    if (!parts.length) return '';
    parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    return parts.length < 2 ? parts[0] : `${parts.slice(0, -1).join(', ')}, or ${parts[parts.length - 1]}`;
  }

  const phoneLine = (phone) => `call our team on <b>${escapeHtml(phone)}</b>`;

  /*
    Mainland only check (Theme settings > Delivery rates > Mainland only
    postcode check), for product pages the delivery block marks
    data-direct: products delivered outside MGP, such as the outdoor
    kitchens (tag direct-dispatch). The answer is worked out here from the
    setting's blocked list, so there is no rates lookup and no MGP call.
    Checkout (Intuitive Shipping) still decides, and its rule must block
    the same postcodes. These defaults match config/settings_schema.json
    and stand in for a setting left blank.
  */
  const DIRECT_BLOCKED = 'BT, IM, GY, JE, HS, ZE, KW15-KW17, IV41-IV49, IV51, IV55-IV56, KA27-KA28, PA20, PA41-PA49, PA60-PA78, PH42-PH44, PO30-PO41, TR21-TR25';
  const DIRECT_OK = "Good news, we deliver outdoor kitchens to [postcode]. Delivery is £49 per order. [preorder] We'll contact you closer to the time to arrange your delivery day.";
  const DIRECT_NO = "Sorry, we can't deliver outdoor kitchens to [postcode]. We deliver them to mainland England, Wales and Scotland.";

  // The blocked list as tests. Entries are separated by commas (or new
  // lines), capitals ignored: an area (BT), a district (IV51), a range of
  // districts in one area (PA60-PA78 or PA60-78), a sector (PA34 5) or a
  // full postcode (EX39 2LY). Anything else is skipped.
  function postcodeList(raw) {
    return String(raw || '').toUpperCase().replace(/[‐-―]/g, '-').split(/[,;\n]+/)
      .map((s) => s.trim().replace(/\s+/g, ' '))
      .filter(Boolean)
      .map((entry) => {
        const compact = entry.replace(/ /g, '');
        let m = compact.match(/^([A-Z]{1,2})(\d{1,2})-([A-Z]{1,2})?(\d{1,2})$/);
        if (m) {
          if (m[3] && m[3] !== m[1]) return null;
          const from = Number(m[2]);
          const to = Number(m[4]);
          return from <= to ? { area: m[1], from, to } : null;
        }
        if (/^[A-Z]{1,2}$/.test(compact)) return { area: compact };
        m = entry.match(/^([A-Z]{1,2}\d[A-Z\d]?) (\d)$/);
        if (m) return { outward: m[1], sector: m[2] };
        if (entry.indexOf(' ') < 0 && UK_OUTWARD.test(compact)) return { outward: compact };
        const pc = normalise(compact);
        return pc && pc.full ? { compact: pc.compact } : null;
      })
      .filter(Boolean);
  }

  // Whether a full postcode (from normalise) is on the list.
  function listHas(list, pc) {
    const area = pc.compact.match(/^[A-Z]{1,2}/)[0];
    const district = pc.outward.match(/^[A-Z]{1,2}(\d{1,2})$/);
    return list.some((t) => {
      if (t.compact) return pc.compact === t.compact;
      if (t.sector) return pc.outward === t.outward && pc.compact.charAt(pc.outward.length) === t.sector;
      if (t.outward) return pc.outward === t.outward;
      if (t.from !== undefined) return !!district && area === t.area && Number(district[1]) >= t.from && Number(district[1]) <= t.to;
      return area === t.area;
    });
  }

  // A setting's answer with [postcode] and [preorder] filled in. The text
  // is escaped first, so a setting cannot add markup; a pre-order line
  // without a full stop gets one.
  function directText(template, pc, preorder) {
    const line = String(preorder || '').trim();
    const said = line && !/[.!?]$/.test(line) ? `${line}.` : line;
    return escapeHtml(template)
      .replace(/\[postcode\]/gi, () => `<b>${escapeHtml(pc.display)}</b>`)
      .replace(/\[preorder\]/gi, () => escapeHtml(said))
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* ---------- Delivery page ---------- */

  function initDeliveryPage(root) {
    const input = root.querySelector('[data-postcode]');
    const button = root.querySelector('[data-check]');
    const geo = root.querySelector('[data-locate]');
    const output = root.querySelector('[data-output]');
    const phone = root.dataset.phone || '01636 821 135';
    const origin = workerOrigin(root.dataset.worker);
    const zonesUrl = root.dataset.zones;
    const buildings = Array.from(root.querySelectorAll('[data-building]'))
      .map((el) => ({ label: el.dataset.label, variant: el.dataset.variant }))
      .filter((b) => b.label && b.variant);
    let zonesPromise = null;
    let busy = false;

    const MSG = {
      mainland: '<b>Good news.</b> Your area is on our standard mainland network, where most buildings are delivered free. You will see the exact delivery cost for your building at checkout.',
      nextDay: ' Next working day delivery is also available here for selected buildings, at an extra charge.',
      surcharge: `Deliveries to your area usually carry a charge, which depends on the building. You will see the exact cost at checkout, or ${phoneLine(phone)} to check before you order.`,
      callUs: `Deliveries to islands and offshore postcodes are arranged by our team. Please ${phoneLine(phone)} and we will confirm what is possible.`,
      unconfirmed: `We could not confirm delivery to this postcode online. Please ${phoneLine(phone)} and we will help.`,
      generic: `Most mainland orders are delivered free. Scotland, Northern Ireland and parts of Wales and the South West carry a charge that depends on the building, and island deliveries are arranged by our team. You will see the exact cost at checkout, or ${phoneLine(phone)}.`,
    };

    const say = (html, muted) => {
      output.innerHTML = html;
      output.classList.toggle('is-muted', !!muted);
    };
    const done = (html, muted) => {
      say(html, muted);
      busy = false;
      button.disabled = false;
    };

    function loadZones() {
      if (!zonesPromise) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), ZONES_TIMEOUT_MS);
        zonesPromise = fetch(zonesUrl, { signal: ctrl.signal }).then((r) => {
          if (!r.ok) throw new Error(`zones ${r.status}`);
          return r.json();
        }).then((z) => {
          if (!z || !['call_us_areas', 'call_us_districts', 'surcharge_areas'].every((key) => Array.isArray(z[key]))) throw new Error('zones payload');
          return z;
        }).finally(() => clearTimeout(timer));
        zonesPromise.catch(() => { zonesPromise = null; });
      }
      return zonesPromise;
    }

    function fromZoneMap(pc) {
      return loadZones()
        .then((z) => {
          const area = pc.compact.match(/^[A-Z]{1,2}/)[0];
          if ((z.call_us_areas || []).includes(area) || (z.call_us_districts || []).includes(pc.outward)) return done(MSG.callUs, true);
          if ((z.surcharge_areas || []).includes(area)) return done(MSG.surcharge, true);
          return done(MSG.mainland, false);
        })
        .catch(() => done(MSG.generic, true));
    }

    async function fromBuildings(pc) {
      const results = await Promise.all(buildings.map((b) => rates(origin, b.variant, pc).then((d) => ({ b, d }), () => ({ b, d: null }))));
      if (results.every((r) => !r.d)) return fromZoneMap(pc);
      const rows = results.map(({ b, d }) => {
        const label = `<b>${escapeHtml(b.label)}:</b> `;
        if (!d) return `<li>${label}we could not check this one just now, please ${phoneLine(phone)}.</li>`;
        if (!d.deliverable) return `<li>${label}cannot be booked online for this postcode, please ${phoneLine(phone)}.</li>`;
        return `<li>${label}${describeOptions(d.options)}</li>`;
      });
      return done(`Delivery to <b>${escapeHtml(pc.display)}</b>:<ul>${rows.join('')}</ul>Prices are per building. The exact cost for your order, including any extras, shows at checkout.`, false);
    }

    async function fromZone(pc) {
      try {
        const d = await zone(origin, pc);
        if (!d.deliverable) return done(MSG.unconfirmed, true);
        return d.free_mainland ? done(MSG.mainland + (d.next_day ? MSG.nextDay : ''), false) : done(MSG.surcharge, true);
      } catch (e) {
        return fromZoneMap(pc);
      }
    }

    function check() {
      if (busy) return;
      if (!input.value.trim()) return say('Please enter your postcode.', true);
      const pc = normalise(input.value);
      if (!pc) return say(`That does not look like a UK postcode. Please check it, or ${phoneLine(phone)}.`, true);
      if (!pc.full) return say(FULL_POSTCODE, true);
      busy = true;
      button.disabled = true;
      remember(pc);
      say('Checking your area...', true);
      if (!origin) return fromZoneMap(pc);
      return buildings.length ? fromBuildings(pc) : fromZone(pc);
    }

    button.addEventListener('click', check);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') check(); });
    const saved = recall();
    if (saved && !input.value) input.value = saved.display;

    if (geo && navigator.geolocation && location.protocol === 'https:') {
      geo.hidden = false;
      geo.addEventListener('click', () => {
        say('Finding your location...', true);
        navigator.geolocation.getCurrentPosition((pos) => {
          fetch(`https://api.postcodes.io/postcodes?lon=${pos.coords.longitude}&lat=${pos.coords.latitude}&limit=1`)
            .then((r) => r.json())
            .then((j) => {
              const found = j && j.result && j.result[0] && j.result[0].postcode;
              if (!found) return say('We could not find a postcode for your location. Please type it in.', true);
              input.value = found;
              return check();
            })
            .catch(() => say('We could not look up your location. Please type your postcode in.', true));
        }, () => say('Location access was declined. Please type your postcode in.', true), { timeout: 8000, maximumAge: 600000 });
      });
    }
  }

  // noun is what the product is, from the block's data-noun: "building"
  // unless the page is an outdoor kitchen, so buildings read as before.
  const ZONE_MSG = {
    mainland: (phone, noun = 'building') => `Your area is on our standard mainland network, where most ${noun === 'building' ? 'buildings' : 'orders'} are delivered free. You will see the exact delivery cost for this ${noun} at checkout, or ${phoneLine(phone)}.`,
    surcharge: (phone, noun = 'building') => `Deliveries to your area usually carry a charge, which depends on the ${noun}. You will see the exact cost at checkout, or ${phoneLine(phone)} to check before you order.`,
    callUs: (phone) => `Deliveries to islands and offshore postcodes are arranged by our team. Please ${phoneLine(phone)} and we will confirm what is possible.`,
    generic: (phone, noun = 'building') => `Most mainland orders are delivered free. Scotland, Northern Ireland and parts of Wales and the South West carry a charge that depends on the ${noun}. You will see the exact cost at checkout, or ${phoneLine(phone)}.`,
  };

  /* ---------- Product page ---------- */

  function initProductPage(root) {
    // Mainland only products (data-direct): answered here, see postcodeList.
    // A blocked list that reads as nothing falls back to the default one.
    const directList = root.hasAttribute('data-direct') ? postcodeList(root.dataset.directBlocked) : null;
    const direct = directList ? {
      list: directList.length ? directList : postcodeList(DIRECT_BLOCKED),
      ok: (root.dataset.directOk || '').trim() || DIRECT_OK,
      no: (root.dataset.directNo || '').trim() || DIRECT_NO,
      preorder: root.dataset.directPreorder || '',
      preorderIds: (root.dataset.directPreorderIds || '').split(',').filter(Boolean),
    } : null;
    const origin = workerOrigin(root.dataset.worker);
    if (!origin && !direct) return;
    const phone = root.dataset.phone || '01636 821 135';
    const toggle = root.querySelector('[data-open]');
    const formLabel = root.querySelector('[data-prompt]');
    const form = root.querySelector('[data-form]');
    const input = root.querySelector('[data-postcode]');
    const output = root.querySelector('[data-output]');
    const deliveryBox = root.closest('.wdel');
    const deliveryDate = deliveryBox && deliveryBox.querySelector('[data-delivery-date]');
    const deliveryLabel = deliveryDate && deliveryDate.querySelector('[data-delivery-label]');
    const deliveryDateValue = deliveryDate && deliveryDate.querySelector('#delivery-date-placeholder');
    // The direct dispatch line under the check: a mainland only answer
    // says more, so the line steps aside while one shows.
    const directLine = direct && deliveryBox ? deliveryBox.querySelector('.wdel__direct') : null;
    let variant = root.dataset.variant;
    let postcode = null;
    let run = 0;
    let debounce = null;
    let priorityRefresh = null;
    // Pick-a-day line (assets/pick-a-day.js, Theme settings > Delivery rates,
    // off by default). Without data-pad or the module every pad step below
    // is a no-op and the checker behaves exactly as before.
    const pad = !direct && root.hasAttribute('data-pad') && window.WaltonsPickADay ? window.WaltonsPickADay : null;
    const padLine = pad ? root.querySelector('[data-pad-line]') : null;
    // Always rendered and visually hidden, so the line is announced even
    // though it appears (display) and gets its text in the same tick.
    const padSay = pad ? root.querySelector('[data-pad-say]') : null;
    let sku = root.dataset.sku || '';
    // Bumped whenever the line is hidden, so an answer still on its way
    // for an earlier check (or a postcode being changed) is dropped.
    // Separate from run, which also guards the rates result.
    let padRun = 0;
    let padTimer = null;
    // What the pick-a-day answer changed in the rates result (the options
    // and the note), as they were, to restore.
    let padUndo = [];
    // What holdForPad took out of the standard rows and the note while MGP
    // answers, to put back in place (the row elements stay, so their
    // entrance animation does not replay).
    let padHold = [];
    // The answer on show, and whether it took the quickest rows away, so
    // they come back if the page is still open when the cut-off passes.
    let padShown = null;
    let padDroppedQuickest = false;
    // Set while MGP's answer is awaited, and kept when it settles Standard
    // delivery (a day to choose, or the team arranges it): the lead time
    // estimate line above the result stays hidden, whatever re-shows it,
    // so Standard delivery only ever carries MGP's date. Never set with
    // pick-a-day off.
    let padOwnsDate = false;

    const show = (html, muted) => {
      closeHelpIn(output);
      output.innerHTML = html;
      output.hidden = !html;
      output.classList.toggle('is-muted', !!muted);
    };
    const stopPriorityRefresh = () => {
      if (priorityRefresh !== null) {
        clearInterval(priorityRefresh);
        priorityRefresh = null;
      }
    };
    const restoreDeliveryDate = () => {
      stopPriorityRefresh();
      if (!deliveryDate || !deliveryLabel || !deliveryDateValue) return;
      deliveryLabel.textContent = deliveryDate.dataset.standardLabel || 'Standard delivery estimate:';
      const standardDate = deliveryDate.dataset.standardDate || '';
      deliveryDateValue.textContent = standardDate;
      deliveryDate.classList.remove('is-priority');
      deliveryDate.hidden = !standardDate || padOwnsDate;
    };
    const renderProductOptions = (options, priorityDate, standardDate) => {
      const seen = new Set();
      return options
        .slice()
        .sort((a, b) => Number(isNextDayOption(b)) - Number(isNextDayOption(a)) || a.price - b.price)
        .filter((option) => {
          const key = `${isNextDayOption(option)}:${option.price}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((option) => {
          const nextDay = isNextDayOption(option);
          const date = nextDay ? priorityDate : standardDate;
          const when = nextDay
            ? (priorityDate ? `<span class="wdel__when" data-priority-date>${escapeHtml(priorityDate)}</span>` : '<span class="wdel__when">Next working day</span>')
            : (standardDate ? `<span class="wdel__when">${escapeHtml(standardDate)}</span>` : '');
          const price = option.price === 0 ? 'Free' : `£${option.price.toFixed(2)}`;
          return `<div class="wdel__option" data-option="${nextDay ? 'next-day' : 'standard'}">${leaf(date)}<span class="wdel__what"><span class="wdel__name">${nextDay ? 'Quickest delivery' : 'Standard delivery'}</span>${when}</span><strong class="wdel__price">${price}</strong></div>`;
        }).join('');
    };
    // Puts back what holdForPad took out: the standard rows' dates and the
    // estimates note. The row elements themselves stay.
    const releaseHold = () => {
      padHold.forEach(({ el, html, hidden }) => {
        if (html !== null) el.innerHTML = html;
        el.hidden = hidden;
      });
      padHold = [];
    };
    const hidePad = () => {
      if (!padLine) return;
      closeHelpIn(output);
      padRun += 1;
      clearTimeout(padTimer);
      padLine.textContent = '';
      padLine.hidden = true;
      if (padSay) padSay.textContent = '';
      releaseHold();
      padUndo.forEach(({ el, html, hidden }) => {
        el.innerHTML = html;
        el.hidden = hidden;
      });
      padUndo = [];
      padShown = null;
      padDroppedQuickest = false;
      padOwnsDate = false;
    };
    const padEdit = (el) => padUndo.push({ el, html: el.innerHTML, hidden: el.hidden });
    // While MGP is still answering (up to PAD_HOLD_MS), the standard rows
    // show the van and no date, and the estimates note waits, so the
    // estimate does not show just before MGP's day replaces it. Each row is
    // changed in place and put back by releaseHold before renderPad.
    const holdForPad = () => {
      const options = output.querySelector('.wdel__options');
      if (options) {
        options.querySelectorAll('[data-option="standard"]').forEach((row) => {
          padHold.push({ el: row, html: row.innerHTML, hidden: row.hidden });
          row.querySelector('.wdel__leaf').outerHTML = leaf('');
          const when = row.querySelector('.wdel__when');
          if (when) when.remove();
        });
      }
      const note = output.querySelector('.wdel__checkout');
      if (note) {
        padHold.push({ el: note, html: null, hidden: note.hidden });
        note.hidden = true;
      }
    };
    // MGP answers for this building alone, so the day is worded "from":
    // add-ons chosen later can move it. Anything but a clear answer leaves
    // the rates result as it is; it never says a postcode cannot be
    // delivered to.
    //  - A day to choose: every standard row becomes "choose your day,
    //    from ...", and the estimates note goes, as nothing left on the
    //    line is an estimate. The quickest row keeps the emphasis.
    //  - MGP's team arranging the day (CUSTOMER_CONTACT): no line, and
    //    every standard row's date becomes "From <day>", MGP's earliest
    //    day, or else the row's own lead time date, whichever is a real
    //    day (productFrom). A row with neither stays as it is. The price
    //    and the quickest row are untouched; the estimates note goes. When
    //    the rates call failed, the line below reads "Standard delivery
    //    from ..." instead.
    //  - MGP refusing next day (nextDay false, never null) on a working
    //    day before the cut-off, and only when Theme settings > Delivery
    //    rates says to (data-pad-hide-next-day): the quickest rows go, as
    //    long as a standard row stays. Off by default, because checkout
    //    (Intuitive Shipping) still sells next day whatever MGP says, and
    //    the product page should offer what checkout sells. After the
    //    cut-off MGP refuses every product, so they always stay then.
    // Otherwise (no standard row, or MGP's own wording) the line below says
    // it. When MGP gives a day or hands the day to the team, the estimate
    // line above the result stays hidden while that answer stands
    // (padOwnsDate).
    const renderPad = (answer) => {
      if (!padLine) return;
      const options = output.querySelector('.wdel__options');
      const standards = options ? Array.from(options.querySelectorAll('[data-option="standard"]')) : [];
      const standard = standards[0];
      const quickest = options ? options.querySelectorAll('[data-option="next-day"]') : [];
      const pick = pad.productPick(answer);
      const day = pick ? pad.formatDay(answer.earliestDate) : '';
      const team = !pick && typeof pad.productContact === 'function' && pad.productContact(answer);
      // Each standard row and the day it reads "From": MGP's, or its own
      // lead time date, whichever is a real day (productFrom).
      const fromRows = team ? standards.map((row) => {
        const when = row.querySelector('.wdel__when');
        return { row, date: pad.productFrom(answer, when ? when.textContent : '') };
      }).filter((r) => r.date) : [];
      const refused = root.hasAttribute('data-pad-hide-next-day') && !!answer && answer.nextDay === false &&
        beforeCutoff(root.dataset.cutoff, root.dataset.holidays);
      padShown = answer;
      if (options && standard && ((pick && day) || (refused && quickest.length) || fromRows.length)) {
        padEdit(options);
        if (refused && quickest.length) {
          quickest.forEach((row) => row.remove());
          padDroppedQuickest = true;
        }
        if (pick && day) {
          standards.forEach((row) => {
            row.classList.add('is-pick');
            row.querySelector('.wdel__leaf').outerHTML = leaf(day);
            row.querySelector('.wdel__what').innerHTML = `<span class="wdel__name">Standard delivery</span>${pickWhen(day)}`;
          });
        }
        fromRows.forEach(({ row, date }) => {
          row.querySelector('.wdel__leaf').outerHTML = leaf(date);
          row.querySelector('.wdel__what').innerHTML = `<span class="wdel__name">Standard delivery</span><span class="wdel__when">From ${escapeHtml(date)}</span>`;
        });
      }
      // The estimates note also goes when MGP's team arranges the day (a
      // "From" day or the direct delivery line shows instead): the team,
      // not an estimate, settles the date.
      const note = output.querySelector('.wdel__checkout');
      const contact = !pick && (team || !!pad.productLine(answer));
      if (note && ((pick && day && standard) || contact)) {
        padEdit(note);
        note.hidden = true;
      }
      // A failed rates call leaves no rows to carry the "From" day, so the
      // line below gives it (MGP's day, or else the lead time date the
      // estimate line holds). Rates offering next day only get no line, as
      // checkout then sells no Standard delivery to name.
      const lineFrom = team && !options ? pad.productFrom(answer, deliveryDate ? deliveryDate.dataset.standardDate : '') : '';
      const fromLine = lineFrom ? `Standard delivery from ${lineFrom}.` : '';
      padOwnsDate = !!((pick && day) || (contact && !team) || (team && (standard || lineFrom)));
      if (padOwnsDate && deliveryDate) deliveryDate.hidden = true;
      const text = pick && day && standard ? '' : fromLine || pad.productLine(answer);
      padLine.textContent = text;
      padLine.hidden = !text;
      let say = text;
      if (pick && day && standard) say = `Standard delivery: choose your day at checkout, from ${day}.`;
      else if (fromRows.length) say = `Standard delivery from ${fromRows[0].date}.`;
      if (padSay && say) {
        const mine = padRun;
        clearTimeout(padTimer);
        padTimer = setTimeout(() => {
          if (mine !== padRun) return;
          padSay.textContent = say;
          // Announced, then cleared: at rest the words are in the box once
          // (the line or the row), not a second time in this sr-only copy.
          padTimer = setTimeout(() => {
            if (mine === padRun) padSay.textContent = '';
          }, 5000);
        }, 150);
      }
    };
    // MGP needs a full postcode; an outward code alone gets no line. Never
    // rejects, so awaiting it inside check()'s try cannot trigger the zone
    // fallback over a good rates result.
    const padCheck = () => {
      if (!padLine || !postcode || postcode.display.indexOf(' ') < 1) return null;
      try {
        return Promise.resolve(pad.availability(postcode.display, [{ sku, quantity: 1 }])).catch(() => null);
      } catch (e) {
        return null;
      }
    };
    const openForm = (focusField = true) => {
      hidePad();
      input.setCustomValidity('');
      restoreDeliveryDate();
      if (directLine) directLine.hidden = false;
      root.classList.remove('is-checked');
      if (toggle) toggle.hidden = true;
      if (formLabel) formLabel.hidden = false;
      form.hidden = false;
      if (postcode) input.value = postcode.display;
      if (focusField) input.focus();
    };
    const changeLink = '<button type="button" class="wdel__change link" data-change>Change postcode</button>';
    const head = () => `<div class="wdel__result-head"><span class="wdel__to">Delivery to <b class="wdel__postcode">${escapeHtml(postcode.display)}</b></span>${changeLink}</div>`;
    const checking = () => `<span class="wdel__loading">Checking delivery to <b>${escapeHtml(postcode.display)}</b></span>`;

    // The mainland only answer, at once: blocked (muted) or delivered,
    // the latter with the pre-order line when this variant is a pre-order.
    // The estimate line and the direct dispatch line stay hidden while it
    // shows; Change postcode (openForm) brings them back.
    function answerDirect() {
      ++run;
      clearTimeout(debounce);
      stopPriorityRefresh();
      const blocked = listHas(direct.list, postcode);
      const preorder = !blocked && direct.preorderIds.includes(String(variant)) ? direct.preorder : '';
      root.classList.add('is-checked');
      form.hidden = true;
      if (toggle) toggle.hidden = true;
      if (formLabel) formLabel.hidden = true;
      if (deliveryDate) deliveryDate.hidden = true;
      if (directLine) directLine.hidden = true;
      show(`${head()}<p>${directText(blocked ? direct.no : direct.ok, postcode, preorder)}</p>`, blocked);
    }

    async function check() {
      if (!postcode || !variant) return;
      if (direct) return answerDirect();
      const mine = ++run;
      hidePad();
      const padMine = padRun;
      // Started alongside the rates call and read only after the rates
      // result is shown, so the pick-a-day line never delays it.
      const padAnswer = padCheck();
      // MGP's answer, not the lead time estimate, decides the Standard
      // date, so the estimate line waits for it.
      if (padAnswer) padOwnsDate = true;
      restoreDeliveryDate();
      root.classList.remove('is-checked');
      form.hidden = true;
      if (toggle) toggle.hidden = true;
      if (formLabel) formLabel.hidden = true;
      show(checking(), true);
      try {
        const d = await rates(origin, variant, postcode);
        if (mine !== run) return;
        root.classList.add('is-checked');
        if (!d.deliverable) {
          padOwnsDate = false;
          restoreDeliveryDate();
          if (deliveryDate) deliveryDate.hidden = true;
          show(`${head()}<p>Online delivery is unavailable for this postcode. Please ${phoneLine(phone)}.</p>`, true);
        } else {
          const hasNextDay = d.options.some(isNextDayOption);
          const priorityDate = hasNextDay ? priorityDeliveryDate(root.dataset.cutoff, root.dataset.holidays) : '';
          const standardDate = deliveryDate && deliveryDate.dataset.standardDate || '';
          if (deliveryDate) deliveryDate.hidden = true;
          show(`${head()}<div class="wdel__options">${renderProductOptions(d.options, priorityDate, standardDate)}</div><p class="wdel__checkout">Dates are estimates. Confirmed at checkout or by our team after you order.</p>`, false);
          if (priorityDate) {
            priorityRefresh = setInterval(() => {
              if (!root.isConnected) return stopPriorityRefresh();
              // Quickest rows taken away before the cut-off come back after it.
              if (padDroppedQuickest && !beforeCutoff(root.dataset.cutoff, root.dataset.holidays)) {
                const answer = padShown;
                hidePad();
                renderPad(answer);
              }
              const nextDate = priorityDeliveryDate(root.dataset.cutoff, root.dataset.holidays);
              const dateElement = output.querySelector('[data-priority-date]');
              if (dateElement && nextDate && dateElement.textContent !== nextDate) {
                dateElement.textContent = nextDate;
                const row = dateElement.closest('.wdel__option');
                const oldLeaf = row && row.querySelector('.wdel__leaf');
                if (oldLeaf) oldLeaf.outerHTML = leaf(nextDate);
              }
            }, 30000);
          }
          if (padAnswer) {
            holdForPad();
            // A slow MGP: the estimate shows after PAD_HOLD_MS, and MGP's
            // answer, when it comes, replaces it.
            const release = setTimeout(() => {
              if (mine === run && padMine === padRun) releaseHold();
            }, PAD_HOLD_MS);
            const answer = await padAnswer;
            clearTimeout(release);
            if (mine !== run || padMine !== padRun) return;
            releaseHold();
            renderPad(answer);
          }
        }
      } catch (e) {
        if (mine !== run) return;
        restoreDeliveryDate();
        // Live rates unavailable: answer from the bundled zone map, the same
        // fallback the Delivery page uses, rather than an error.
        const message = await zoneMessage(postcode);
        if (mine !== run) return;
        root.classList.add('is-checked');
        show(`${head()}<p>${message}</p>`, true);
        if (padAnswer) {
          // A slow MGP: the estimate line shows after PAD_HOLD_MS, as with
          // the rows above.
          const release = setTimeout(() => {
            if (mine !== run || padMine !== padRun) return;
            padOwnsDate = false;
            restoreDeliveryDate();
          }, PAD_HOLD_MS);
          const answer = await padAnswer;
          clearTimeout(release);
          if (mine !== run || padMine !== padRun) return;
          renderPad(answer);
          // No day and no team line from MGP: the estimate line is back,
          // as before pick-a-day.
          if (!padOwnsDate) restoreDeliveryDate();
        }
      }
    }

    let zonesPromise = null;
    const noun = root.dataset.noun || 'building';
    function zoneMessage(pc) {
      const zonesUrl = root.dataset.zones;
      if (!zonesUrl) return Promise.resolve(ZONE_MSG.generic(phone, noun));
      if (!zonesPromise) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), ZONES_TIMEOUT_MS);
        zonesPromise = fetch(zonesUrl, { signal: ctrl.signal })
          .then((r) => { if (!r.ok) throw new Error(`zones ${r.status}`); return r.json(); })
          .finally(() => clearTimeout(timer));
        zonesPromise.catch(() => { zonesPromise = null; });
      }
      return zonesPromise.then((z) => {
        const area = pc.compact.match(/^[A-Z]{1,2}/)[0];
        if ((z.call_us_areas || []).includes(area) || (z.call_us_districts || []).includes(pc.outward)) return ZONE_MSG.callUs(phone);
        if ((z.surcharge_areas || []).includes(area)) return ZONE_MSG.surcharge(phone, noun);
        return ZONE_MSG.mainland(phone, noun);
      }).catch(() => ZONE_MSG.generic(phone, noun));
    }

    function submit(e) {
      e.preventDefault();
      const pc = normalise(input.value);
      if (!pc) {
        input.setCustomValidity('That does not look like a UK postcode. Please check it.');
        input.reportValidity();
        return;
      }
      if (!pc.full) {
        input.setCustomValidity(FULL_POSTCODE);
        input.reportValidity();
        return;
      }
      input.setCustomValidity('');
      postcode = pc;
      remember(pc);
      check();
    }

    function onVariant(next) {
      if (!next || String(next.id) === String(variant)) return;
      ++run;
      clearTimeout(debounce);
      variant = String(next.id);
      // The postcode is waiting in the field (nothing checked on this page
      // yet) and no answer is kept for this size: it keeps waiting for Check.
      // A mainland only answer costs nothing, so it is always "kept".
      const waiting = !!postcode && !form.hidden && (direct ? true : !storedAnswer(rateUrl(origin, variant, postcode)));
      if (padLine) {
        sku = next.sku || '';
        hidePad();
        // The check below asks MGP about the new building, so no estimate
        // line meanwhile (see check()).
        padOwnsDate = next.available !== false && !!postcode && !waiting;
      }
      restoreDeliveryDate();
      root.hidden = next.available === false;
      if (directLine && root.hidden) directLine.hidden = false;
      if (root.hidden || !postcode || waiting) return;
      if (direct) return answerDirect();
      show(checking(), true);
      debounce = setTimeout(check, 250);
    }

    if (toggle) toggle.addEventListener('click', openForm);
    form.addEventListener('submit', submit);
    input.addEventListener('input', () => input.setCustomValidity(''));
    if (deliveryDate) deliveryDate.addEventListener('waltons:delivery-date-updated', restoreDeliveryDate);
    output.addEventListener('click', (e) => {
      if (e.target.closest('[data-change]')) {
        show('', false);
        openForm();
      }
    });

    const sectionId = root.dataset.section;
    if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event && event.data;
        if (data && (!sectionId || data.sectionId === sectionId)) onVariant(data.variant);
      });
    }

    postcode = recall();
    // An outward code saved before full postcodes were needed is offered
    // back in the field rather than checked.
    if (postcode && !postcode.full) {
      input.value = postcode.display;
      postcode = null;
    }
    // On load the remembered postcode is only answered from a kept answer
    // for this building. Otherwise it waits in the field for the shopper to
    // press Check, so browsing buildings does not ask the worker (and make
    // a hidden Shopify cart) on every page. A size change after an answer
    // is showing still checks straight away (onVariant). A mainland only
    // product answers a remembered postcode at once, as that asks nobody.
    if (postcode && root.dataset.available !== 'false' && variant && (direct || storedAnswer(rateUrl(origin, variant, postcode)))) check();
    else if (postcode && root.dataset.available !== 'false') openForm(false);
    else if (formLabel) openForm(false);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-delivery-check]:not([data-ready])').forEach((el) => {
      el.dataset.ready = '';
      initDeliveryPage(el);
    });
    (scope || document).querySelectorAll('[data-product-delivery-check]:not([data-ready])').forEach((el) => {
      el.dataset.ready = '';
      initProductPage(el);
    });
  }

  window.WaltonsDelivery = { init, normalise, describeOptions, workerOrigin, priorityDeliveryDate, postcodeList, listHas, directText };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
